import {useCallback, useEffect, useLayoutEffect, useState} from 'react';
import {List} from 'immutable';

import {getFeatureFlagStatus} from '../FeatureFlag/FeatureFlag.react';

import bulkCreateSchoolAccounts from './useCreateAccounts.queries';
import legacyBulkCreateSchoolAccounts from './legacyUserCreateAccounts';
/**
 * @typedef {import('../../resources/GeneratedModels/EmailDomain/EmailDomainModel.v1').EmailDomainModelV1} EmailDomain
 * @typedef {import('../../resources/augmented/School/SchoolModel.v5').SchoolModelV5} School
 * @typedef {Map} AccountRecord
 * @property {number} id - Index or other unique identifier to give each Map its own Immutable signature in case of duplicates
 * @property {string} lastName - Accountholder's surname
 * @property {string} firstName - Accountholder's given name
 * @property {string} email - Accountholder's email address
 * @property {string} username - Accountholder's username, 4-16 alphanumeric characters long
 * @property {string} password - Accountholder's password, at least 6 characters long
 * @property {?string} error - The key of the AccountRecord found to be erroneous
 * @property {?string} errorMessage - An explanation of the erroneous key
 * @typedef {object} StateAndHelpers
 * @property {List<AccountRecord>} archivedInvalidEntries - Running list of AccountRecords, decorated with error and errorMessage attributes
 * @property {Function} clearUseCreateAccounts - a cleanup function resetting all hook state
 * @property {Error} error - API error
 * @property {boolean} isPending - Indicates whether the creation request is pending
 * @property {object} response - API response
 * @property {List<AccountRecord>} validEntries - Running list of validated AccountRecords
 */

const initialState = {
  entries: List(),
  invalidEntries: List(),
  validEntries: List(),
  error: null
};

/**
 * useCreateAccounts hook
 *
 * @param {List<AccountRecord>} accounts - The accounts to create
 * @param {object} config - Setup instructions for validating account records
 * @param {string} config.coppaConsentGrantorEmail - The administrator's email address
 * @param {List<EmailDomain>} config.emailDomains - The managed email domains belonging to this school
 * @param {School} config.school - The school to which the new accounts will belong
 * @param config.callback
 * @returns {StateAndHelpers} stateAndHelpers - request status indicators, account records sorted by valid and invalid, and a cleanup function
 */

const featureFlagName = 'sca_error_order';

export default function useCreateAccounts(
  accounts = List(),
  {callback = () => {}, coppaConsentGrantorEmail, emailDomains, school}
) {
  const [isPending, setIsPending] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [results, setResults] = useState(initialState);
  const [isLegacy, setIsLegacy] = useState(true);

  const clearUseCreateAccounts = useCallback(() => {
    setIsPending(false);
    setResults(initialState);
    setAttempts(0);
  }, []);

  useEffect(() => {
    const fetchFlagStatus = async () => {
      try {
        const isEnabled = await getFeatureFlagStatus(featureFlagName);
        setIsLegacy(!isEnabled);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        // setIsLoading(false);
      }
    };

    fetchFlagStatus();
  }, []);

  /**
   * Parses an array of records into a JSON:API payload
   *
   * @param {Array} records - An array of arrays
   * @returns {Array.<object>} payload - An array of objects representing new user records
   */

  useLayoutEffect(() => {
    if (accounts.isEmpty() /* || isPending || response */) {
      return;
    }

    const createAccounts = async () => {
      setAttempts((a) => a + 1);
      setIsPending(true);
      const bulkCreateFunc = isLegacy ? legacyBulkCreateSchoolAccounts : bulkCreateSchoolAccounts;
      const bulkCreateResults = await bulkCreateFunc(
        accounts,
        emailDomains,
        coppaConsentGrantorEmail,
        school
      );
      callback();

      setResults((prev) => {
        return {
          ...prev,
          ...bulkCreateResults
        };
      });

      setIsPending(false);
    };

    if (attempts < 1) {
      createAccounts();
    }
  }, [accounts, attempts, callback, emailDomains, coppaConsentGrantorEmail, school, isLegacy]);

  return {
    // TODO return results as one object?
    archivedInvalidEntries: results.invalidEntries,
    clearUseCreateAccounts,
    error: results.error,
    isPending,
    response: results.response,
    validEntries: results.validEntries
  };
}
